import React from 'react';
import { EntryIcon } from './EntryIcon';
import EntryActionsSheet, { EntryAction } from './EntryActionsSheet';
import { MoreHorizontal } from 'lucide-react';
import { EntryListItemProps } from '../types';
import { getEntryTitle } from '../utils';
import { entryTypeStyles } from '../types';
import { useHasAnyPermission } from '../../../../hooks/usePermission';
import type { PermissionString } from '../../../../types/permissions';
import { useAuth } from '../../../../context/authcontext';
import ReasonDialog from '../../../common/ReasonDialog';
import { useToast } from '../../../common/Toast';
import { describeApiError } from '../../../../utils/apiErrors';
import { formatStaff, StaffDirectory, useStaffDirectoryContext } from '../StaffDirectoryContext';
import { useChartForm } from '../ChartFormContext';
import { entryVisibility, VISIBILITY_ENTRY_TYPES } from '../model/entryMeta';
import { InternalChip } from './forms/SmallControls';
import EntrySheet from './EntrySheet';
import type { Entry } from '../types';

/** "Jane Doe, LVN"; the provider text when it is a name; "Staff member" otherwise (M5) */
export const authorLabel = (entry: Entry, resolve?: StaffDirectory['resolve']): string => {
  const known = formatStaff(resolve?.(entry.createdBy));
  if (known) return known;
  const provider = (entry.provider || '').trim();
  return provider && /\s/.test(provider) && provider !== 'Current Provider' ? provider : 'Staff member';
};

/** M7: a PRN dose that was given and has no follow-up yet */
export const canRecordEffect = (entry: Entry, visitEntries: Entry[]): boolean => {
  if (entry.type !== 'mar' || entry.invalidated || !entry.data || typeof entry.data !== 'object') return false;
  const d = entry.data as { status?: string; followUpOf?: string; indication?: string; prn?: boolean };
  if (d.followUpOf || (d.status && d.status !== 'GIVEN')) return false;
  if (!d.indication && !d.prn) return false;
  return !visitEntries.some((e) => !e.invalidated && e.data && typeof e.data === 'object' && String((e.data as { followUpOf?: string }).followUpOf || '') === String(entry.id));
};

// Utility function to format dates in mm/dd/yyyy hh:mm am/pm format in local timezone
const formatDateToLocal = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    // Format hours for 12-hour clock with AM/PM
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM
    const hoursStr = String(hours).padStart(2, '0');
    
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original on error
  }
};

const EntryListItem: React.FC<EntryListItemProps> = ({
  entry,
  handleEditEntry,
  handleFlagEntry,
  handleInvalidateEntry,
  inPlaceEditingEntryId,
  setInPlaceEditingEntryId,
  renderEntryContent,
  renderSelectedForm,
  cancelInPlaceEdit,
  handleSaveInPlaceEdit,
  isMobile,
  saving = false,
  locked = false,
  visitEnded = false,
  highlightEntryId = null
}) => {
  const { user } = useAuth();
  const directory = useStaffDirectoryContext();
  const chart = useChartForm();
  const { showToast } = useToast();
  // Permission check for charting update
  const canUpdateCharting = useHasAnyPermission([
    'patientmr:phi:charting:update' as PermissionString,
    'patientmr:phi:charting:updateall' as PermissionString
  ]);
  const [visibilityTarget, setVisibilityTarget] = React.useState<'CHART' | 'INTERNAL' | null>(null);
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const [changingVisibility, setChangingVisibility] = React.useState(false);

  // Determine if this specific entry is being edited
  const isEditing = String(inPlaceEditingEntryId) === String(entry.id);
  // Phase 18U U4: the entry just saved is scrolled into view and highlighted for 2 s
  const highlighted = highlightEntryId !== null && String(highlightEntryId) === String(entry.id);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const [flash, setFlash] = React.useState(false);
  React.useEffect(() => {
    if (!highlighted) return undefined;
    rowRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 2000);
    return () => clearTimeout(t);
  }, [highlighted]);
  const isPast = visitEnded;
  // Phase 9C: only the author edits clinical content; addenda are never edited
  const isAuthor = !entry.createdBy || !user?.id || String(entry.createdBy) === String(user.id);
  const canEdit = isAuthor && entry.type !== 'addendum';
  // Phase 18 M6: internal entries carry a lock chip; a supervisor may change another author's (reason required)
  const internal = entryVisibility(entry) === 'INTERNAL';
  const supportsVisibility = (VISIBILITY_ENTRY_TYPES as readonly string[]).includes(entry.type);
  const showVisibilityChange = Boolean(
    chart?.changeVisibility && chart.canSuperviseVisibility && supportsVisibility && !isAuthor && !entry.invalidated && !locked && !isEditing
  );
  const showRecordEffect = Boolean(
    chart?.recordEffect && chart.chartEditable && !locked && !isEditing && canUpdateCharting && canRecordEffect(entry, chart.visitEntries)
  );
  // Phase 18 M1: Save stays disabled while the edited form is not valid
  const editInvalid = isEditing && chart ? Object.keys({ ...chart.errorsFor(entry.type), ...chart.serverFieldErrors }).length > 0 : false;
  const observedOrDate = entry.observedAt || entry.date;

  // Phase 18U U10: the entry's actions, in the order a nurse needs them
  const anotherEditOpen = inPlaceEditingEntryId !== null && String(inPlaceEditingEntryId) !== String(entry.id);
  const actionable = !entry.invalidated && !locked && canUpdateCharting;
  const entryActions: EntryAction[] = [
    ...(actionable && canEdit ? [{ key: 'edit' as const, label: 'Edit', hint: isPast ? 'Changes after the visit are saved as late or amended.' : undefined, disabled: anotherEditOpen, onSelect: () => handleEditEntry(entry) }] : []),
    ...(showRecordEffect ? [{ key: 'effect' as const, label: 'Record effect of this PRN dose', onSelect: () => chart?.recordEffect?.(entry) }] : []),
    ...(showVisibilityChange ? [{ key: 'visibility' as const, label: internal ? 'Make chart-visible' : 'Make internal', hint: 'A reason is required.', onSelect: () => setVisibilityTarget(internal ? 'CHART' : 'INTERNAL') }] : []),
    ...(actionable ? [{ key: 'flag' as const, label: 'Flag for review', onSelect: () => handleFlagEntry(entry.id) }] : []),
    ...(actionable ? [{ key: 'invalidate' as const, label: 'Invalidate', hint: 'The entry stays in the chart, struck through, with your reason.', destructive: true, onSelect: () => handleInvalidateEntry(entry.id) }] : []),
  ];

  const confirmVisibilityChange = async (reason: string) => {
    if (!visibilityTarget || !chart?.changeVisibility) return;
    try {
      setChangingVisibility(true);
      await chart.changeVisibility(entry, visibilityTarget, reason);
      setVisibilityTarget(null);
      showToast(visibilityTarget === 'INTERNAL' ? 'Entry marked internal.' : 'Entry is now part of the external chart.', { variant: 'success' });
    } catch (error) {
      showToast(describeApiError(error, 'Failed to change the visibility. Please try again.'), { variant: 'error' });
    } finally {
      setChangingVisibility(false);
    }
  };
  
  const styles = (entryTypeStyles as Record<string, typeof entryTypeStyles.vitals>)[entry.type] || {
    bg: 'bg-white',
    border: 'border-gray-200',
    iconColor: 'text-gray-700',
    headerBg: 'bg-gray-100/50'
  };
  
  return (
    <div
      key={entry.id}
      ref={rowRef}
      tabIndex={-1}
      data-entry-id={String(entry.id)}
      data-highlighted={flash ? 'true' : undefined}
      className={`${styles.bg} ${isMobile ? 'p-1.5' : 'p-2'} rounded border mb-1 relative transition-all duration-150 ${
        isEditing ? `${styles.border} bg-opacity-30` : ''
      } ${flash ? 'ring-2 ring-emerald-500 ring-offset-1' : ''} focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-600`}
    >
      <div className="flex justify-between items-center -m-1 mb-1 p-2">
        <h4 className="text-sm font-medium flex items-center">
          <EntryIcon type={entry.type} />
          <span className="ml-1">{getEntryTitle(entry.type)}</span>
          {internal && <InternalChip />}
          <span className="ml-2 text-xs text-gray-500">
            {formatDateToLocal(observedOrDate)}
            <span className="ml-1 font-medium text-gray-700" data-testid="entry-author">· {authorLabel(entry, directory?.resolve)}</span>
            {entry.lastEdited && (
              <span className="text-xs italic text-gray-600 ml-1">
                (edited {formatDateToLocal(entry.lastEdited)})
              </span>
            )}
          </span>
        </h4>
        
        <div className="flex">
          {isEditing ? (
            // Phase 18U U3: the edit form is in the sheet below; the row only says so
            <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-semibold text-white" data-testid="entry-editing-chip">Editing</span>
          ) : (
            <div className="flex items-center gap-1">
            {showRecordEffect && (
              <button
                type="button"
                onClick={() => chart?.recordEffect?.(entry)}
                className="min-h-[36px] rounded border border-green-400 bg-white px-2 text-xs font-medium text-green-800 hover:bg-green-50"
              >
                Record effect
              </button>
            )}
            {/* Phase 18U U10: every action of the entry in one labelled sheet (replaces the "⋮" menu) */}
            {entryActions.length > 0 && (
              <button
                type="button"
                onClick={() => setActionsOpen(true)}
                aria-label={`Actions for ${getEntryTitle(entry.type)} ${formatDateToLocal(observedOrDate)}`}
                className={`flex items-center gap-1 rounded border border-gray-300 bg-white px-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${isMobile ? 'min-h-[44px]' : 'min-h-[32px]'}`}
              >
                <MoreHorizontal size={16} aria-hidden="true" />Actions
              </button>
            )}
            </div>
          )}
        </div>
      </div>
      
      {/* Content Area - Show either the edit form or the content display */}
      <div className="mt-1 text-left w-full">
        {renderEntryContent(entry)}
      </div>

      {/* Phase 18U U3: the edit form opens in a sheet with Save pinned in the footer */}
      {isEditing && (
        <EntrySheet
          open
          entryType={entry.type}
          mode="edit"
          onCancel={cancelInPlaceEdit}
          onSave={() => handleSaveInPlaceEdit(entry.id)}
          saving={saving}
          invalid={editInvalid}
          touch={isMobile}
        >
          {renderSelectedForm(entry.id)}
        </EntrySheet>
      )}

      <EntryActionsSheet
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        title={getEntryTitle(entry.type)}
        subtitle={`${formatDateToLocal(observedOrDate)} · ${authorLabel(entry, directory?.resolve)}`}
        actions={entryActions}
      />

      {/* Phase 18 M6: a supervisor changes the visibility of another author's entry */}
      <ReasonDialog
        open={visibilityTarget !== null}
        title={visibilityTarget === 'INTERNAL' ? 'Mark entry internal' : 'Show entry on external copies'}
        description={visibilityTarget === 'INTERNAL'
          ? 'Internal entries are not shown to the family or on external copies. They stay part of the record.'
          : 'The entry will be shown on external copies of the chart and, where allowed, to the family.'}
        label="Reason for the change"
        confirmLabel={visibilityTarget === 'INTERNAL' ? 'Mark internal' : 'Show externally'}
        busy={changingVisibility}
        onConfirm={confirmVisibilityChange}
        onCancel={() => setVisibilityTarget(null)}
      />
    </div>
  );
};

export default EntryListItem;
